import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionType, TransactionTypeValue } from '../../../../services/transaction.types';

interface TransactionTypeModalProps {
    open: boolean;
    onClose: () => void;
    onSelect: (type: TransactionType) => void;
    transactionTypes: TransactionType[];
}

// Main transaction types that should be shown first
const MAIN_TRANSACTION_TYPES = [
    TransactionTypeValue.Trade,
    TransactionTypeValue.InIncome,
    TransactionTypeValue.OutSpend,
    TransactionTypeValue.Transfer,
    TransactionTypeValue.InDeposit,
    TransactionTypeValue.OutWithdrawal,
];

// Move TypeCard outside to prevent recreation on every render
const TypeCard = React.memo(({ type, onSelect }: { type: TransactionType; onSelect: (type: TransactionType) => void }) => {
    const { t } = useTranslation();
    // Show description for all transaction types including Mining (commercial)
    const shouldShowDescription = true;
    const [isHovered, setIsHovered] = useState(false);

    // Debug hover state
    React.useEffect(() => {
        console.log('[TypeCard] Hover state changed for', type.name, ':', isHovered);
    }, [isHovered, type.name]);

    const handleCardSelect = useCallback((selectedType: TransactionType) => {
        onSelect(selectedType);
    }, [onSelect]);

    return (
        <Paper
            elevation={0}
            variant="outlined"
            sx={{
                p: 2,
                cursor: 'pointer !important',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                // Apply hover styles conditionally based on state instead of CSS :hover
                ...(isHovered && {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                })
            }}
            onMouseUp={() => {
                // Use MouseUp as reliable click trigger since onClick doesn't work in production
                setTimeout(() => {
                    handleCardSelect(type);
                }, 10);
            }}
            onMouseEnter={() => {
                console.log('[TypeCard] MouseEnter - setting hover to true for:', type.name);
                setIsHovered(true);
            }}
            onMouseLeave={() => {
                console.log('[TypeCard] MouseLeave - setting hover to false for:', type.name);
                setIsHovered(false);
            }}
        >
            <Typography variant="subtitle1" sx={{
                fontWeight: 700,
                textAlign: 'center',
                mb: shouldShowDescription ? 0.5 : 0,
                color: 'text.primary'
            }}>
                {type.name}
            </Typography>
            {shouldShowDescription && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        textAlign: 'center',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.2,
                        opacity: 0.85,
                        fontWeight: 400
                    }}
                >
                    {t(`transactions.typeModal.descriptions.${type.value === TransactionTypeValue.Trade ? 'Trade' :
                       type.value === TransactionTypeValue.InIncome ? 'InIncome' :
                       type.value === TransactionTypeValue.OutSpend ? 'OutSpend' :
                       type.value === TransactionTypeValue.Transfer ? 'Transfer' :
                       type.value === TransactionTypeValue.InDeposit ? 'InDeposit' :
                       type.value === TransactionTypeValue.OutWithdrawal ? 'OutWithdrawal' :
                       type.value === TransactionTypeValue.InMining ? 'InMining' :
                       type.value === TransactionTypeValue.InStaking ? 'InStaking' :
                       type.value === TransactionTypeValue.InAirdrop ? 'InAirdrop' :
                       type.value === TransactionTypeValue.OutDonation ? 'OutDonation' :
                       type.value === TransactionTypeValue.OutGift ? 'OutGift' :
                       type.value === TransactionTypeValue.InGiftTip ? 'InGiftTip' :
                       type.value === TransactionTypeValue.OutProvideLiquidity ? 'OutProvideLiquidity' :
                       type.value === TransactionTypeValue.InRemoveLiquidity ? 'InRemoveLiquidity' :
                       type.value === TransactionTypeValue.InRewards ? 'InRewards' :
                       type.value === TransactionTypeValue.InIncomeNonTaxable ? 'InIncomeNonTaxable' :
                       type.value === TransactionTypeValue.InOtherIncomeNonTaxable ? 'InOtherIncomeNonTaxable' :
                       type.value === TransactionTypeValue.OutStolen ? 'OutStolen' :
                       type.value === TransactionTypeValue.OutLost ? 'OutLost' :
                       type.value === TransactionTypeValue.OutOtherFee ? 'OutOtherFee' :
                       type.value === TransactionTypeValue.OutExpenseNonTaxable ? 'OutExpenseNonTaxable' :
                       type.value === TransactionTypeValue.SwapWrapNonTaxable ? 'SwapWrapNonTaxable' :
                       type.value === TransactionTypeValue.InMasternode ? 'InMasternode' :
                       type.value === TransactionTypeValue.InMiningCommercial ? 'InMiningCommercial' :
                       type.value === TransactionTypeValue.InMinting ? 'InMinting' :
                       type.value === TransactionTypeValue.InDividends ? 'InDividends' :
                       type.value === TransactionTypeValue.InLending ? 'InLending' :
                       type.value === TransactionTypeValue.InInterest ? 'InInterest' :
                       type.value === TransactionTypeValue.InOther ? 'InOther' :
                       type.value === TransactionTypeValue.OutBorrowingFee ? 'OutBorrowingFee' :
                       type.value === TransactionTypeValue.OutOtherExpenses ? 'OutOtherExpenses' :
                       type.value === TransactionTypeValue.InLpIncome ? 'InLpRewards' :
                       type.value === TransactionTypeValue.InReceiveLpToken ? 'InReceiveLpToken' :
                       type.value === TransactionTypeValue.OutReturnLpToken ? 'OutReturnLpToken' :
                       type.value === TransactionTypeValue.InReceiveLoan ? 'InReceiveLoan' :
                       type.value === TransactionTypeValue.InRemoveCollateral ? 'InRemoveCollateral' :
                       type.value === TransactionTypeValue.OutAddCollateral ? 'OutSendCollateral' :
                       type.value === TransactionTypeValue.OutRepayLoan ? 'OutRepayLoan' :
                       type.value === TransactionTypeValue.OutLiquidation ? 'OutLiquidation' :
                       type.value === TransactionTypeValue.InMarginProfit ? 'MarginProfit' :
                       type.value === TransactionTypeValue.OutMarginLoss ? 'MarginLoss' :
                       type.value === TransactionTypeValue.OutMarginFee ? 'OutMarginFee' :
                       type.value === TransactionTypeValue.InDerivativesProfit ? 'DerivativesFuturesProfit' :
                       type.value === TransactionTypeValue.OutDerivativesLoss ? 'DerivativesFuturesLoss' :
                       type.value === TransactionTypeValue.OutSettlementFee ? 'OutSettlementFee' :
                       type.value === TransactionTypeValue.InAirdropNonTaxable ? 'InAirdropNonTaxable' :
                       ''}`, '')}
                </Typography>
            )}
        </Paper>
    );
});

const TransactionTypeModal: React.FC<TransactionTypeModalProps> = ({ open, onClose, onSelect, transactionTypes }) => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');

    // Split transaction types into main and other categories
    const { mainTypes, otherTypes } = useMemo(() => {
        const main = transactionTypes
            .filter((type) => MAIN_TRANSACTION_TYPES.includes(type.value))
            .sort((a, b) => {
                // Sort based on the order in MAIN_TRANSACTION_TYPES
                return MAIN_TRANSACTION_TYPES.indexOf(a.value) - MAIN_TRANSACTION_TYPES.indexOf(b.value);
            });
        const other = transactionTypes.filter((type) => !MAIN_TRANSACTION_TYPES.includes(type.value));
        return { mainTypes: main, otherTypes: other };
    }, [transactionTypes]);

    // Filter and group types based on search query
    const filteredAndGroupedTypes = useMemo(() => {
        const filterByQuery = (types: TransactionType[]) => {
            if (!searchQuery.trim()) return types;
            
            const query = searchQuery.toLowerCase().trim();
            return types.filter((type) => {
                // Try to get translated name using the type value
                const translatedName = t(`transactions.types.${type.value}.name`, '').toLowerCase();
                
                // Get the correct description translation key based on the type value
                const descriptionKey = `transactions.typeModal.descriptions.${
                    type.value === TransactionTypeValue.Trade ? 'Trade' : 
                    type.value === TransactionTypeValue.InIncome ? 'InIncome' : 
                    type.value === TransactionTypeValue.OutSpend ? 'OutSpend' : 
                    type.value === TransactionTypeValue.Transfer ? 'Transfer' : 
                    type.value === TransactionTypeValue.InDeposit ? 'InDeposit' : 
                    type.value === TransactionTypeValue.OutWithdrawal ? 'OutWithdrawal' : 
                    type.value === TransactionTypeValue.InMining ? 'InMining' : 
                    type.value === TransactionTypeValue.InStaking ? 'InStaking' : 
                    type.value === TransactionTypeValue.InAirdrop ? 'InAirdrop' : 
                    type.value === TransactionTypeValue.OutDonation ? 'OutDonation' : 
                    type.value === TransactionTypeValue.OutGift ? 'OutGift' : 
                    type.value === TransactionTypeValue.InGiftTip ? 'InGiftTip' : 
                    type.value === TransactionTypeValue.OutProvideLiquidity ? 'OutProvideLiquidity' : 
                    type.value === TransactionTypeValue.InRemoveLiquidity ? 'InRemoveLiquidity' : 
                    type.value === TransactionTypeValue.InRewards ? 'InRewards' :
                    type.value === TransactionTypeValue.InIncomeNonTaxable ? 'InIncomeNonTaxable' :
                    type.value
                }`;
                
                const translatedDescription = t(descriptionKey, '').toLowerCase();
                
                // Check if any of these properties match the query
                return (
                    // Check translated text first (if available)
                    (translatedName && translatedName.includes(query)) ||
                    (translatedDescription && translatedDescription.includes(query)) ||
                    // Fall back to direct properties
                    type.name.toLowerCase().includes(query) || 
                    type.group.toLowerCase().includes(query) ||
                    type.subgroup.toLowerCase().includes(query) ||
                    type.subgroupName.toLowerCase().includes(query)
                );
            });
        };

        // Filter out Transfer type only (keep Mining commercial)
        const filteredTransactionTypes = otherTypes.filter(
            type => type.value !== TransactionTypeValue.Transfer
        );

        const groupBySubgroup = (types: TransactionType[]) => {
            const grouped = types.reduce(
                (acc, type) => {
                    const key = type.subgroup;
                    if (!acc[key]) {
                        acc[key] = {
                            subgroupName: type.subgroupName,
                            types: [],
                        };
                    }
                    acc[key].types.push(type);
                    return acc;
                },
                {} as Record<string, { subgroupName: string; types: TransactionType[] }>
            );

            // Define custom order for subgroups
            const subgroupOrder = ['incoming', 'outgoing', 'liquidity', 'other', 'advanced'];
            
            // Sort groups by custom order
            return Object.entries(grouped).sort((a, b) => {
                const indexA = subgroupOrder.indexOf(a[0]);
                const indexB = subgroupOrder.indexOf(b[0]);
                
                // If both are in the order array, sort by their position
                if (indexA !== -1 && indexB !== -1) {
                    return indexA - indexB;
                }
                
                // If only one is in the order array, prioritize it
                if (indexA !== -1) return -1;
                if (indexB !== -1) return 1;
                
                // Otherwise, fall back to alphabetical sorting
                return a[1].subgroupName.localeCompare(b[1].subgroupName);
            });
        };

        const filteredMain = filterByQuery(mainTypes);
        const filteredOther = filterByQuery(filteredTransactionTypes);
        const groupedOther = groupBySubgroup(filteredOther);

        return {
            mainTypes: filteredMain,
            groupedOtherTypes: groupedOther,
        };
    }, [mainTypes, otherTypes, searchQuery]);

    const handleSelect = useCallback((type: TransactionType) => {
        onSelect(type);
        onClose();
    }, [onSelect, onClose]);









    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle sx={{
                p: 3,
                pb: 2 // Increased bottom padding for more space
            }}>
                <Stack spacing={3}> {/* Increased spacing between title and search bar */}
                    <Typography variant="h6">
                        {t('transactions.typeModal.title')}
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                    }}>
                        <TextField
                            size="small"
                            placeholder={t('transactions.typeModal.searchPlaceholder')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 1
                                }
                            }}
                        />
                    </Box>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{
                p: 3,
                pt: 4
            }}>
                <Box sx={{
                    width: '100%'
                }}>
                    {/* Main Types Section */}
                    {filteredAndGroupedTypes.mainTypes.length > 0 && (
                        <>
                            <Typography variant="subtitle2" sx={{
                                mb: 2,
                                fontWeight: 600
                            }}>
                                {t('transactions.typeModal.commonTypes')}
                            </Typography>
                            <Grid container spacing={2} sx={{
                                mb: 3
                            }}>
                                {filteredAndGroupedTypes.mainTypes.map((type) => (
                                    <Grid item xs={12} sm={6} md={4} key={type.value}>
                                        <TypeCard type={type} onSelect={handleSelect} />
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )}

                    {/* Other Types Section - Always visible now */}
                    {filteredAndGroupedTypes.groupedOtherTypes.map(
                        ([subgroup, group]) =>
                            group.types.length > 0 && (
                                <Box key={subgroup} sx={{
                                    mb: 4
                                }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            mb: 2,
                                            fontWeight: 600
                                        }}
                                    >
                                        {t(`transactions.typeModal.groups.${subgroup}`, group.subgroupName)}
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {group.types.map((type) => (
                                            <Grid item xs={12} sm={6} md={4} key={type.value}>
                                                <TypeCard type={type} onSelect={handleSelect} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default TransactionTypeModal;
